import MatestackUiVueJs from "matestack-ui-vuejs";

// Sidecar for Web::Components::ReadingAids. No template of its own: the
// article body is v-pre, so everything is found by selector on mount.
// Matestack swaps the page DOM on transitions, so state lives on the instance
// and is torn down in beforeUnmount.
const readingAids = {
  mixins: [MatestackUiVueJs.componentMixin],
  template: MatestackUiVueJs.componentHelpers.inlineTemplate,
  mounted() {
    this.article = document.querySelector(".prose-article");
    if (!this.article) return;
    this.links = Array.from(document.querySelectorAll("[data-outline-id]"));
    // Only headings the outline lists: an h4 (or any heading without a link)
    // would become "current" and leave no link highlighted.
    const listed = new Set(this.links.map((link) => link.dataset.outlineId));
    this.headings = Array.from(this.article.querySelectorAll("h2[id], h3[id]")).filter((h) => listed.has(h.id));
    this.strip = document.getElementById("reading-progress");
    this.bar = null;
    if (this.strip) {
      this.bar = document.createElement("div");
      this.bar.className = "bg-primary h-full";
      this.bar.style.width = "0%";
      this.strip.appendChild(this.bar);
    }
    this.frame = null;
    this.current = undefined;
    this.reported = false;
    this.articleId = this.$el && this.$el.dataset ? this.$el.dataset.articleId : null;
    this.articleTitle = this.$el && this.$el.dataset ? this.$el.dataset.articleTitle : null;
    // Highlight and progress both update once per frame while scrolling. (An
    // IntersectionObserver missed jumps that carried a heading straight past
    // the top band, such as following a link to a deeper heading.)
    this.onScroll = () => {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.highlight();
        this.progress();
      });
    };
    window.addEventListener("scroll", this.onScroll, { passive: true });
    window.addEventListener("resize", this.onScroll, { passive: true });
    this.highlight();
    this.progress();
  },
  beforeUnmount() {
    if (this.onScroll) {
      window.removeEventListener("scroll", this.onScroll);
      window.removeEventListener("resize", this.onScroll);
    }
    if (this.frame) cancelAnimationFrame(this.frame);
    if (this.bar) this.bar.remove();
    this.bar = this.frame = null;
  },
  methods: {
    // The current section is the last heading that has reached the top 30% of
    // the viewport, or the article title when none has.
    highlight() {
      const limit = window.innerHeight * 0.3;
      let current = this.links[0] ? this.links[0].dataset.outlineId : null;
      for (const h of this.headings) {
        if (h.getBoundingClientRect().top <= limit) current = h.id;
      }
      // Scroll fires many times a frame's worth; touch the DOM only when the
      // current section actually changes.
      if (current === this.current) return;
      this.current = current;
      this.links.forEach((link) => {
        if (link.dataset.outlineId === current) link.setAttribute("data-current", "true");
        else link.removeAttribute("data-current");
      });
    },
    progress() {
      if (!this.bar) return;
      const rect = this.article.getBoundingClientRect();
      const span = rect.height - window.innerHeight;
      const fraction = span > 0 ? -rect.top / span : rect.top < 0 ? 1 : 0;
      this.bar.style.width = `${Math.min(1, Math.max(0, fraction)) * 100}%`;
      if (fraction >= 0.9) this.reportRead();
    },
    // Once per article view: tells Ahoy the reader reached the end (Admin →
    // Stats, "Read to the end"). Ahoy's API takes the visit from the
    // Ahoy-Visit / Ahoy-Visitor headers (the same tokens as its cookies), and
    // ties the event to the page view's visit. Keith's own reads are dropped
    // server-side.
    reportRead() {
      if (this.reported || !this.articleId) return;
      this.reported = true;
      const visit = this.cookie("ahoy_visit");
      const visitor = this.cookie("ahoy_visitor");
      if (!visit || !visitor) return;
      const body = JSON.stringify({
        events: [{ name: "Read article", properties: { id: Number(this.articleId), title: this.articleTitle }, time: new Date().toISOString() }],
      });
      fetch("/ahoy/events", {
        method: "POST",
        credentials: "same-origin",
        keepalive: true,
        headers: { "Content-Type": "application/json", "Ahoy-Visit": visit, "Ahoy-Visitor": visitor },
        body,
      }).catch(() => {});
    },
    cookie(name) {
      const match = document.cookie.split("; ").find((part) => part.startsWith(`${name}=`));
      return match ? decodeURIComponent(match.slice(name.length + 1)) : null;
    },
  },
};

export default readingAids;
